import { Alert, Button, Flex, Text } from '@rocket/ui';
import { useState } from 'react';
import { useOnboarding } from '../OnboardingContext';

/**
 * Global, always-mounted notice that a rule-extraction job from a previous
 * session is still running or just finished. `OnboardingContext` discovers
 * this on every session start regardless of FTUE eligibility, but the wizard
 * that can resume/review it (`OnboardingPage`) only mounts for first-time
 * users -- so a returning user with an in-flight extraction had no surface
 * that could ever show it. This banner is the surface: it lives alongside
 * the host's other global chrome (toasts, FTUE overlay) and, on Resume,
 * hands off into the wizard via `resumeExtraction()`.
 *
 * Renders null when there's nothing to resume.
 */
export function ResumableExtractionBanner() {
  const { resumableExtraction, resumeExtraction, discardResumableExtraction } = useOnboarding();
  const [isDiscarding, setIsDiscarding] = useState(false);
  const [discardError, setDiscardError] = useState<string | null>(null);

  if (!resumableExtraction) {
    return null;
  }

  const filenames = resumableExtraction.filenames?.join(', ') || 'a previous upload';

  const handleDiscard = async () => {
    setIsDiscarding(true);
    setDiscardError(null);
    const didCancel = await discardResumableExtraction(resumableExtraction.jobId);
    if (!didCancel) {
      setDiscardError('Could not discard the job. Please try again.');
    }
    setIsDiscarding(false);
  };

  return (
    <Alert status="info" title="Extraction still in progress">
      Rules are being extracted from {filenames}. You can pick back up where it left off, or discard
      it and start over.
      <Flex.H gap="2" mt="2">
        <Button size="xs" colorPalette="blue" onClick={resumeExtraction}>
          Resume
        </Button>
        <Button
          size="xs"
          variant="outline"
          onClick={handleDiscard}
          disabled={isDiscarding}
          loading={isDiscarding}
        >
          Discard
        </Button>
      </Flex.H>
      {discardError && (
        <Text fontSize="xs" color="red.fg" mt="1">
          {discardError}
        </Text>
      )}
    </Alert>
  );
}
